import * as React from 'react';
import './styles.css';
import Close from '@material-ui/icons/Close';
import ArrowForward from '@material-ui/icons/ArrowForward';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import { Loader } from '../Loader/Loader';
import withStyles, { WithStyles } from '@material-ui/core/styles/withStyles';
import { globalStyles } from 'src/theme';
import { observer } from 'mobx-react';
import FormGroup from '@material-ui/core/FormGroup';
import MuiPickersUtilsProvider from 'material-ui-pickers/MuiPickersUtilsProvider';
import { InlineDatePicker } from 'material-ui-pickers/DatePicker';
import { KeyboardArrowRight, KeyboardArrowLeft } from '@material-ui/icons';
import { DateTime } from 'luxon';
import { dateFormatted } from '../../datetime';
import LuxonUtils from '@date-io/luxon';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import IconButton from '@material-ui/core/IconButton';
import { createStyles, Theme } from '@material-ui/core/styles';
import { OrderSplitByDate, ChildOrder } from 'src/models/order-split-by-date';
import { Order } from 'src/models/order';
import { RelatedOrder } from 'src/models/related-order';

export interface ISplitOrderByDeliveryDateDialogProps extends WithStyles<typeof styles> {
    onClose(): void;
    onSubmit(orderSplit: OrderSplitByDate): void;
    parentOrder: Order;
}

@observer
class SplitOrderByDeliveryDateDialog extends React.Component<ISplitOrderByDeliveryDateDialogProps> {
    private orderSplit: OrderSplitByDate;

    constructor(props: ISplitOrderByDeliveryDateDialogProps) {
        super(props);
        this.orderSplit = new OrderSplitByDate(props.parentOrder);
        this.handleSubmit = this.handleSubmit.bind(this);
        this.handleAddGroup = this.handleAddGroup.bind(this);
    }

    public render() {
        const { classes } = this.props;

        return (
            <div className="full-dialog split-order-by-date-dialog">
                <Loader area="split-order-by-date-dialog" />
                <div className="dialog-content">
                    <h3>Split Order #{this.props.parentOrder.orderNumber}</h3>

                    {/* Show editable forms for current split */}
                    {this.orderSplit.childOrders.map((childOrder, index) => (
                        <React.Fragment key={index}>
                        <div className={`split-order-child-order${index === 0 ? ' parent' : ''}`}>
                            <div className="split-order-card-header">
                                <h4>
                                    {index === 0 ? 'Parent Order (Current)' : `New Child Order`}
                                </h4>
                                {index > 0 && (
                                    <IconButton
                                        onClick={() => this.handleRemoveGroup(index)}
                                        title="Remove this order"
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                )}
                            </div>

                            {this.renderChildOrderErrors(index)}

                            {index === 0 ? (
                                // Parent: read-only delivery date display
                                <FormGroup row className="split-order-date-group">
                                    <div className="split-order-delivery-date-display">
                                        <strong>Delivery Date:</strong>
                                        <span>
                                            {childOrder.dateTba
                                                ? 'TBA'
                                                : childOrder.deliveryDate
                                                    ? dateFormatted(childOrder.deliveryDate)
                                                    : 'Not set'}
                                        </span>
                                    </div>
                                </FormGroup>
                            ) : (
                                // New child orders: editable date picker
                                <FormGroup row className="split-order-date-group">
                                    <MuiPickersUtilsProvider utils={LuxonUtils}>
                                        <InlineDatePicker
                                            className={classes.inlineDateField}
                                            onlyCalendar
                                            label={childOrder.dateTba ? "Date is TBA" : "Delivery Date"}
                                            value={childOrder.deliveryDate}
                                            onChange={(date: DateTime) => childOrder.setDeliveryDate(date.startOf('day'))}
                                            labelFunc={(date: DateTime) => dateFormatted(date)}
                                            InputProps={{ disableUnderline: true }}
                                            leftArrowIcon={<KeyboardArrowLeft />}
                                            rightArrowIcon={<KeyboardArrowRight />}
                                            maxDate={DateTime.local().plus({ months: 6 })}
                                            minDate={DateTime.local().minus({ days: 1 })}
                                            disabled={childOrder.dateTba}
                                        />
                                    </MuiPickersUtilsProvider>
                                    <FormControlLabel
                                        className={classes.checkBox}
                                        control={
                                            <Checkbox
                                                checked={childOrder.dateTba}
                                                onChange={(e: React.ChangeEvent<HTMLInputElement>, checked: boolean) =>
                                                    childOrder.setDateTba(checked)
                                                }
                                            />
                                        }
                                        label="TBA"
                                    />
                                </FormGroup>
                            )}

                            {index === 0
                                ? this.renderParentItemsTable()
                                : this.renderChildItemsTable(childOrder, index)
                            }
                        </div>
                        {index === 0 && this.props.parentOrder.splitOrders.map((childOrder) => (
                            <div key={`existing-${childOrder.id}`} className="split-order-existing-child-order">
                                <div className="split-order-card-header">
                                    <h4>
                                        Existing Child Order #{childOrder.orderNumber}
                                    </h4>
                                </div>

                                <FormGroup row className="split-order-date-group">
                                    <div className="split-order-delivery-date-display">
                                        <strong>Delivery Date:</strong>
                                        <span>
                                            {childOrder.orderDeliveryDateTba
                                                ? 'TBA'
                                                : childOrder.orderDeliveryDate
                                                    ? dateFormatted(childOrder.orderDeliveryDate)
                                                    : 'Not set'}
                                        </span>
                                    </div>
                                </FormGroup>

                                {this.renderExistingChildOrderTable(childOrder)}
                            </div>
                        ))}
                        </React.Fragment>
                    ))}

                    {this.orderSplit.childOrders.length < 2 && (
                        <Button variant="outlined" color="primary" onClick={this.handleAddGroup} className="split-order-add-btn">
                            <AddIcon />
                            Add Child Order
                        </Button>
                    )}
                </div>
                <div className="dialog-actions">
                    <div className="close" onClick={this.props.onClose}>
                        <span>Close</span>
                        <Close className="icon" />
                    </div>
                    <div
                        className={this.orderSplit.isValid ? "done" : "done disabled"}
                        onClick={this.handleSubmit}
                    >
                        <span>Split Order</span>
                        <ArrowForward className="icon" />
                    </div>
                </div>
            </div>
        );
    }

    private renderChildOrderErrors(index: number) {
        const errors = this.orderSplit.getValidationErrorsForChildOrder(index);
        if (errors.length === 0) return null;
        return (
            <ul className="split-order-validation-errors">
                {errors.map((error, i) => <li key={i}>{error}</li>)}
            </ul>
        );
    }

    private renderExistingChildOrderTable(childOrder: RelatedOrder) {
        return (
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell>Product</TableCell>
                        <TableCell align="right">Quantity</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {childOrder.productLines.map((line) => (
                        <TableRow key={`child-product-${line.id}`}>
                            <TableCell>{line.name}</TableCell>
                            <TableCell align="right">
                                <strong>{line.quantity}</strong>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        );
    }

    // Read-only table for the parent order showing original qty and computed remaining
    private renderParentItemsTable() {
        const { parentOrder } = this.props;

        return (
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell>Product</TableCell>
                        <TableCell align="right">Original Qty</TableCell>
                        <TableCell align="right">Remaining Qty</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {parentOrder.productLines.map((line) => {
                        const remaining = this.orderSplit.getParentQuantityForProductLine(line.id);
                        return (
                            <TableRow key={`product-${line.id}`}>
                                <TableCell>{line.name}</TableCell>
                                <TableCell align="right">{line.quantity}</TableCell>
                                <TableCell align="right">
                                    <strong style={{ color: remaining === 0 ? '#f44336' : 'inherit' }}>
                                        {remaining}
                                    </strong>
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        );
    }

    // Editable table for new child orders; qty is capped by what remains in the parent
    private renderChildItemsTable(childOrder: ChildOrder, childOrderIndex: number) {
        const { parentOrder } = this.props;

        return (
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell>Product</TableCell>
                        <TableCell align="right">Qty to Allocate</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {parentOrder.productLines.map((line) => {
                        const max = this.orderSplit.getMaxQuantityForChildOrder(childOrderIndex, line.id);
                        return (
                            <TableRow key={`product-${line.id}`}>
                                <TableCell>{line.name}</TableCell>
                                <TableCell align="right">
                                    <TextField
                                        type="number"
                                        value={childOrder.getQuantityForProductLine(line.id) || ''}
                                        onChange={(e) => {
                                            const raw = parseFloat(e.target.value) || 0;
                                            const value = Math.round(raw * 1000) / 1000;
                                            childOrder.setProductQuantity(line.id, Math.min(value, max));
                                        }}
                                        inputProps={{
                                            min: 0,
                                            max,
                                            step: 0.001,
                                            style: { textAlign: 'right', width: '80px' }
                                        }}
                                    />
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        );
    }

    private handleAddGroup() {
        this.orderSplit.addChildOrder();
    }

    private handleRemoveGroup(index: number) {
        this.orderSplit.removeChildOrder(index);
    }

    private handleSubmit() {
        if (!this.orderSplit.isValid) return;
        this.props.onSubmit(this.orderSplit);
    }
}

const styles = (theme: Theme) =>
    createStyles({
        ...globalStyles,
        checkBox: {
            marginTop: -14,
            marginRight: 0
        }
    });

export default withStyles(styles)(SplitOrderByDeliveryDateDialog);
